import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { IconChart, IconCheckCircle, IconEdit, IconPlus, IconTrash } from '../../../common/icons';
import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import LoadMore from '../../../common/components/LoadMore/LoadMore';
import { useConfirm } from '../../../common/components/ConfirmDialog/confirm';
import ClientFarmSearch from '../../clients/forms/search';
import SectionTabs from '../../navigation/section-tabs';
import { ANIMAL_TABS } from '../../navigation/tabs';
import { useRecipientsInfinite, useDeleteRecipient } from '../contexts/recipients';
import RecipientForm from '../forms/recipient-form';

import '../../../common/styles/catalog.css';
import '../forms/styles/form.css';

// Receptoras (admin-recipients.md) como tab de la sección Animales. Entidad liviana,
// identidad LOCAL a la finca. Filtros server-side: q (número/nombre), farm_id
// (identidad) / client_id (según lo elegido en el buscador compuesto), status.
const STATUS_LABEL = { 2: 'Activa', 1: 'Inactiva', 0: 'Eliminada' };

export default function RecipientsOverview() {
    // Filtros en la URL: q, client_id XOR farm_id (identidad), status. `active` solo
    // guarda el label del chip del buscador compuesto; al montar desde una URL
    // compartida el chip cae a un label genérico (#id).
    const [params, setParams] = useSearchParams();
    const urlSearch = params.get('q') || '';
    const urlClient = params.get('client_id') || '';
    const urlFarm = params.get('farm_id') || '';
    const urlStatus = params.get('status') || '';
    const scopeId = urlFarm || urlClient;

    const [search, setSearch] = useState(urlSearch);
    const [debounced, setDebounced] = useState(urlSearch);
    const [active, setActive] = useState(null);                          // label del chip del buscador compuesto
    const [form, setForm] = useState(null);                              // null | 'new' | recipient(edit)

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [search]);
    useEffect(() => {
        const next = new URLSearchParams(params);
        if (debounced) next.set('q', debounced); else next.delete('q');
        setParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debounced]);

    const setParam = (key, value) => {
        const next = new URLSearchParams(params);
        if (value) next.set(key, value); else next.delete(key);
        setParams(next, { replace: true });
    };

    const { mutateAsync: remove, isPending: removing } = useDeleteRecipient();
    const confirm = useConfirm();

    const filters = useMemo(() => {
        const out = { sort: 'number' };
        if (debounced) out.q = debounced;
        if (urlClient) out.client_id = urlClient;
        if (urlFarm) out.farm_id = urlFarm;
        if (urlStatus) out.status = urlStatus;
        return out;
    }, [debounced, urlClient, urlFarm, urlStatus]);

    const { data, isLoading, isFetching, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useRecipientsInfinite(filters);
    const recipients = useMemo(() => data?.pages?.flatMap(p => p.items ?? []) ?? [], [data]);
    const total = data?.pages?.[0]?.pagination?.total ?? recipients.length;
    const hasFilters = !!(search || scopeId || urlStatus);

    const pick = (it, type) => {
        const next = new URLSearchParams(params);
        if (type === 'farm') {
            next.set('farm_id', String(it.id));
            next.delete('client_id');
            setActive({ type, label: `${it.name}${it.owner?.name ? ` · ${it.owner.name}` : ''}` });
        } else {
            next.set('client_id', String(it.id));
            next.delete('farm_id');
            setActive({ type, label: it.name });
        }
        setParams(next, { replace: true });
    };
    const clearScope = () => {
        const next = new URLSearchParams(params);
        next.delete('client_id');
        next.delete('farm_id');
        setParams(next, { replace: true });
        setActive(null);
    };
    const clearAll = () => { setSearch(''); setActive(null); setParams(new URLSearchParams(), { replace: true }); };

    const del = async (r) => {
        const ok = await confirm({
            title: 'Eliminar receptora',
            message: `¿Eliminar la receptora "${r.number}"${r.farm?.name ? ` de ${r.farm.name}` : ''}?`,
            confirmLabel: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try { await remove(r.id); toast.success('Receptora eliminada'); }
        catch (er) { if (er?.status !== 403) toast.error(er?.message || 'No se pudo eliminar'); }
    };

    return (
        <div className="cat-overview">
            <SectionTabs tabs={ANIMAL_TABS} />
            <header className="cat-overview__header">
                <div className="cat-overview__heading">
                    <h1 className="cat-overview__title">Receptoras</h1>
                    <span className="cat-overview__count">{total}</span>
                </div>
                <Gate domain="recipients" action="write">
                    <button type="button" className="cat-overview__add" onClick={() => setForm('new')}>
                        <IconPlus />
                        <span>Nueva receptora</span>
                    </button>
                </Gate>
            </header>

            <div className="cat-overview__toolbar">
                <div className="cat-overview__search">
                    <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por número o nombre…" aria-label="Buscar receptoras por número o nombre" />
                </div>

                <div className="rcp-filter">
                    {scopeId ? (
                        <span className="rcp-chip">
                            <IconCheckCircle />
                            <span className="rcp-chip__label">{urlFarm ? 'Finca' : 'Cliente'}</span>
                            {active?.label || `#${scopeId}`}
                            <button type="button" className="rcp-chip__clear" onClick={clearScope} aria-label="Quitar filtro">×</button>
                        </span>
                    ) : (
                        <ClientFarmSearch scope="all" placeholder="Filtrar por cliente o finca…" onSelect={pick} />
                    )}
                </div>

                <select className="cat-overview__filter" value={urlStatus} onChange={(e) => setParam('status', e.target.value)} aria-label="Filtrar por estado">
                    <option value="">Activas e inactivas</option>
                    <option value="2">Activa</option>
                    <option value="1">Inactiva</option>
                </select>

                {hasFilters && <button type="button" className="cat-overview__clear" onClick={clearAll}>Limpiar</button>}
            </div>

            {error && <div className="cat-overview__error">{error.status === 403 ? 'No tenés permiso para ver las receptoras.' : (error.message || 'No se pudieron cargar las receptoras')}</div>}

            {isLoading ? (
                <div className="cat-overview__state">Cargando receptoras…</div>
            ) : !recipients.length ? (
                <div className="cat-overview__state">
                    <p className="cat-overview__state-text">{hasFilters ? 'Ninguna receptora coincide con el filtro.' : 'Aún no hay receptoras.'}</p>
                </div>
            ) : (
                <div className={`cat-overview__card ${isFetching ? 'cat-overview__card--busy' : ''}`}>
                    <table className="cat-table">
                        <thead>
                            <tr><th>Número</th><th>Nombre</th><th>Finca</th><th>Cliente</th><th>Estado</th><th aria-label="Acciones" /></tr>
                        </thead>
                        <tbody>
                            {recipients.map((r) => (
                                <tr key={r.id} className="cat-table__row">
                                    <td><Link to={`/admin/recipients/${r.id}/stats`} className="cat-table__name" title="Ver KPIs de la receptora">{r.number || `#${r.id}`}</Link></td>
                                    <td>{r.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{r.farm?.name || <span className="cat-table__empty">—</span>}</td>
                                    <td>{r.client?.name || <span className="cat-table__empty">—</span>}</td>
                                    <td><span className={`cat-badge cat-badge--${r.status}`}>{STATUS_LABEL[r.status] || '—'}</span></td>
                                    <td>
                                        <span className="rcp-tools">
                                            <Link to={`/admin/recipients/${r.id}/stats`} className="rcp-icon" aria-label="KPIs" title="KPIs de la receptora"><IconChart /></Link>
                                            <Gate domain="recipients" action="write">
                                                <button type="button" className="rcp-icon" onClick={() => setForm(r)} disabled={removing} aria-label="Editar" title="Editar"><IconEdit /></button>
                                                <button type="button" className="rcp-icon rcp-icon--danger" onClick={() => del(r)} disabled={removing} aria-label="Eliminar" title="Eliminar"><IconTrash /></button>
                                            </Gate>
                                        </span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {!isLoading && !error && recipients.length > 0 && (
                <LoadMore
                    shown={recipients.length}
                    total={total}
                    hasMore={!!hasNextPage}
                    loading={isFetchingNextPage}
                    onMore={() => fetchNextPage()}
                    noun="receptoras"
                />
            )}

            {form && <RecipientForm recipient={form === 'new' ? null : form} onClose={() => setForm(null)} onSaved={() => setForm(null)} />}
        </div>
    );
}
